import { audio } from '../audio/instances';
import { t, getLocale, setLocale } from '../i18n';
import type { Locale } from '../i18n';
import { PANEL_BG } from '../theme';
import { gameSettings } from './gameSettings';
import { isDesktopDisplay, getDisplay, setMode, onDisplayChange } from './displaySettings';
import { loadRenderResolution, setRenderResolution, screenSize } from './renderResolution';
import { renderPresetsFor, type DisplayMode, type DisplayState, type Size } from '../desktop/display';

/**
 * Modal settings dialog with two volume sliders (music + SFX) and a mute
 * toggle. Lives outside any scene's DOM — opens on demand, cleans up its
 * own overlay on close. Settings persist via the audio singleton.
 */
export class SettingsDialog {
  private overlay: HTMLDivElement | null = null;
  private onCloseCb: (() => void) | null = null;
  private displayUnsub: (() => void) | null = null;

  open(onClose?: () => void, fromMainMenu = false): void {
    if (this.overlay) return;
    this.onCloseCb = onClose ?? null;

    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(8,10,14,0.78)',
      zIndex: '2000',
      pointerEvents: 'auto',
    } as Partial<CSSStyleDeclaration>);

    const card = document.createElement('div');
    Object.assign(card.style, {
      background: PANEL_BG,
      border: '1px solid #2a3140',
      borderRadius: '8px',
      padding: '36px 48px',
      minWidth: '380px',
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
      color: '#d8dee9',
    } as Partial<CSSStyleDeclaration>);

    const title = document.createElement('div');
    title.textContent = t('settings.title');
    Object.assign(title.style, {
      fontSize: '22px',
      fontWeight: '600',
      textAlign: 'center',
      marginBottom: '6px',
    } as Partial<CSSStyleDeclaration>);
    card.appendChild(title);

    // Language selector (only from main menu).
    if (fromMainMenu) {
      const langRow = makeRow();
      langRow.appendChild(makeLabel(t('settings.language')));
      const langSelect = document.createElement('select');
      Object.assign(langSelect.style, {
        padding: '5px 8px', fontSize: '13px',
        background: '#1a1f2a', border: '1px solid #2a3140',
        color: '#d8dee9', borderRadius: '4px',
        fontFamily: 'inherit', cursor: 'pointer', flex: '1',
      } as Partial<CSSStyleDeclaration>);
      const locales: { value: Locale; label: string }[] = [
        { value: 'en', label: 'English' },
        { value: 'pl', label: 'Polski' },
      ];
      for (const loc of locales) {
        const opt = document.createElement('option');
        opt.value = loc.value;
        opt.textContent = loc.label;
        langSelect.appendChild(opt);
      }
      langSelect.value = getLocale();
      langSelect.addEventListener('change', () => {
        setLocale(langSelect.value as Locale);
        // Re-open dialog so all labels refresh with new locale.
        // setLocale dispatches 'locale-changed' which lets the parent
        // scene rebuild independently; we just re-render ourselves.
        const cb = this.onCloseCb;
        this.displayUnsub?.();
        this.displayUnsub = null;
        this.overlay?.remove();
        this.overlay = null;
        this.open(cb ?? undefined, true);
      });
      langRow.appendChild(langSelect);
      card.appendChild(langRow);
    }

    // Mute toggle.
    const muteRow = makeRow();
    muteRow.appendChild(makeLabel(t('settings.muteAll')));
    const muteToggle = document.createElement('button');
    muteToggle.textContent = audio.settings.muted ? `🔇 ${t('settings.muted')}` : `🔊 ${t('settings.soundOn')}`;
    Object.assign(muteToggle.style, {
      background: audio.settings.muted ? '#3a2026' : '#1a3a26',
      color: '#d8dee9',
      border: '1px solid #2a3140',
      padding: '6px 12px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: '13px',
    } as Partial<CSSStyleDeclaration>);
    muteToggle.addEventListener('click', () => {
      const m = audio.toggleMuted();
      muteToggle.textContent = m ? `🔇 ${t('settings.muted')}` : `🔊 ${t('settings.soundOn')}`;
      muteToggle.style.background = m ? '#3a2026' : '#1a3a26';
    });
    muteRow.appendChild(muteToggle);
    card.appendChild(muteRow);

    // Music volume slider.
    const musicRow = makeRow();
    const musicLabel = makeLabel(`🎵 ${t('settings.music')}`);
    const musicValue = makeValueLabel(`${Math.round(audio.settings.musicVolume * 100)}%`);
    musicRow.append(musicLabel, makeSlider(audio.settings.musicVolume, (v) => {
      audio.setMusicVolume(v);
      musicValue.textContent = `${Math.round(v * 100)}%`;
    }), musicValue);
    card.appendChild(musicRow);

    // SFX volume slider.
    const sfxRow = makeRow();
    const sfxLabel = makeLabel(`🔔 ${t('settings.sfx')}`);
    const sfxValue = makeValueLabel(`${Math.round(audio.settings.sfxVolume * 100)}%`);
    sfxRow.append(sfxLabel, makeSlider(audio.settings.sfxVolume, (v) => {
      audio.setSfxVolume(v);
      sfxValue.textContent = `${Math.round(v * 100)}%`;
      // Audible preview when sliding.
      audio.sfx.play('ui-click');
    }), sfxValue);
    card.appendChild(sfxRow);

    // AI animation speed slider (1x - 5x).
    const aiSpeedRow = makeRow();
    const aiSpeedLabel = makeLabel(`⏩ ${t('settings.aiSpeed')}`);
    const fmtAiSpeed = (v: number) => `${v.toFixed(1)}x`;
    const aiSpeedValue = makeValueLabel(fmtAiSpeed(gameSettings.aiAnimSpeed));
    aiSpeedRow.append(aiSpeedLabel, makeSlider((gameSettings.aiAnimSpeed - 0.5) / 4.5, (v) => {
      gameSettings.aiAnimSpeed = Math.max(0.5, Math.round((v * 4.5 + 0.5) * 10) / 10);
      aiSpeedValue.textContent = fmtAiSpeed(gameSettings.aiAnimSpeed);
    }), aiSpeedValue);
    card.appendChild(aiSpeedRow);

    // Scroll speed slider (1x - 3x).
    const scrollRow = makeRow();
    const scrollLabel = makeLabel(`🖱 ${t('settings.scrollSpeed')}`);
    const scrollValue = makeValueLabel(`${gameSettings.scrollSpeed.toFixed(1)}x`);
    scrollRow.append(scrollLabel, makeSlider(gameSettings.scrollSpeed / 3, (v) => {
      gameSettings.scrollSpeed = Math.max(0.5, Math.round(v * 3 * 10) / 10);
      scrollValue.textContent = `${gameSettings.scrollSpeed.toFixed(1)}x`;
    }), scrollValue);
    card.appendChild(scrollRow);

    // Display section — desktop (Electron) only.
    if (isDesktopDisplay()) this.appendDisplaySection(card);

    // Action buttons row — "Save & exit" + "Cancel" with image backgrounds.
    const btnRow = document.createElement('div');
    Object.assign(btnRow.style, {
      display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '12px',
    } as Partial<CSSStyleDeclaration>);

    const settBtnCss = `
      display:inline-block;height:46px;border:none;cursor:pointer;
      background-size:100% 100%;background-repeat:no-repeat;background-color:transparent;
      color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;
      font-weight:bold;font-size:13px;letter-spacing:2px;text-transform:uppercase;
      line-height:46px;text-align:center;
      transition:filter 0.15s,transform 0.08s;
    `.replace(/\n\s*/g, '');

    const style = document.createElement('style');
    style.textContent = `
      .sett-btn:hover { filter: brightness(1.2); }
      .sett-btn:active { filter: brightness(0.9); transform: scale(0.97); }
    `;
    card.appendChild(style);

    const saveBtn = document.createElement('button');
    saveBtn.className = 'sett-btn';
    saveBtn.textContent = t('settings.saveAndClose');
    Object.assign(saveBtn.style.cssText = `${settBtnCss}width:160px;background-image:url('/graphics/ui/confirmation-button-background.png')`, {});
    saveBtn.addEventListener('click', () => this.close());

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'sett-btn';
    cancelBtn.textContent = t('settings.cancel');
    Object.assign(cancelBtn.style.cssText = `${settBtnCss}width:150px;background-image:url('/graphics/ui/cancel-button-background.png')`, {});
    cancelBtn.addEventListener('click', () => this.close());

    btnRow.appendChild(saveBtn);
    btnRow.appendChild(cancelBtn);
    card.appendChild(btnRow);

    overlay.appendChild(card);
    document.body.appendChild(overlay);
    this.overlay = overlay;

    // UI click feedback for any button inside the dialog.
    overlay.addEventListener(
      'click',
      (e) => {
        const t = e.target as HTMLElement;
        if (t && t.tagName === 'BUTTON') audio.sfx.play('ui-click');
      },
      true,
    );
  }

  /**
   * Sekcja „Wyświetlanie" — desktop only. Przełącznik trybu (pełny ekran / okno)
   * i wybór rozdzielczości (rozmiaru okna). Zmiany aplikowane na żywo przez IPC;
   * persistencja po stronie procesu main. Stan ładowany asynchronicznie i
   * odświeżany przy zmianach wywołanych poza dialogiem (np. Esc z pełnego ekranu).
   */
  private appendDisplaySection(card: HTMLDivElement): void {
    const sectionTitle = document.createElement('div');
    sectionTitle.textContent = t('settings.display');
    Object.assign(sectionTitle.style, {
      fontSize: '15px', fontWeight: '600', marginTop: '8px', opacity: '0.9',
    } as Partial<CSSStyleDeclaration>);
    card.appendChild(sectionTitle);

    // Tryb wyświetlania.
    const modeRow = makeRow();
    modeRow.appendChild(makeLabel(t('settings.displayMode')));
    const modeSelect = makeSelect();
    for (const o of [
      { value: 'fullscreen' as DisplayMode, label: t('settings.fullscreen') },
      { value: 'windowed' as DisplayMode, label: t('settings.windowed') },
    ]) {
      const opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label;
      modeSelect.appendChild(opt);
    }
    modeRow.appendChild(modeSelect);
    card.appendChild(modeRow);

    // Rozdzielczość RENDEROWANIA — zawsze aktywna (także w pełnym ekranie).
    // To ustawienie czysto renderowe (Phaser FIT), niezależne od trybu okna.
    const resRow = makeRow();
    resRow.appendChild(makeLabel(t('settings.resolution')));
    const resSelect = makeSelect();
    resRow.appendChild(resSelect);
    card.appendChild(resRow);

    const sizeKey = (s: Size) => `${s.width}x${s.height}`;
    const screen = screenSize();
    const current = loadRenderResolution();
    const options = renderPresetsFor(screen).map((p) => ({ key: sizeKey(p), label: `${p.width} × ${p.height}` }));
    options.push({ key: sizeKey(screen), label: `${t('settings.native')} (${screen.width} × ${screen.height})` });
    const seen = new Set<string>();
    for (const o of options) {
      if (seen.has(o.key)) continue;
      seen.add(o.key);
      const opt = document.createElement('option');
      opt.value = o.key;
      opt.textContent = o.label;
      resSelect.appendChild(opt);
    }
    // Jeśli zapisana rozdzielczość nie jest presetem ani natywną, dołóż ją jako opcję.
    if (!seen.has(sizeKey(current))) {
      const opt = document.createElement('option');
      opt.value = sizeKey(current);
      opt.textContent = `${current.width} × ${current.height}`;
      resSelect.appendChild(opt);
    }
    resSelect.value = sizeKey(current);
    resSelect.addEventListener('change', () => {
      const [w, h] = resSelect.value.split('x').map(Number);
      setRenderResolution(w, h);
    });

    // Tryb wyświetlania (pełny ekran/okno) — Electron, przez IPC.
    const syncMode = (state: DisplayState): void => { modeSelect.value = state.mode; };
    modeSelect.addEventListener('change', () => {
      void setMode(modeSelect.value as DisplayMode).then((s) => { if (s) syncMode(s); });
    });
    void getDisplay().then((s) => { if (s) syncMode(s); });
    this.displayUnsub = onDisplayChange((s) => syncMode(s));
  }

  close(): void {
    this.displayUnsub?.();
    this.displayUnsub = null;
    this.overlay?.remove();
    this.overlay = null;
    if (this.onCloseCb) this.onCloseCb();
    this.onCloseCb = null;
  }

  isOpen(): boolean {
    return this.overlay !== null;
  }
}

function makeRow(): HTMLDivElement {
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '13px',
  } as Partial<CSSStyleDeclaration>);
  return row;
}

function makeSelect(): HTMLSelectElement {
  const s = document.createElement('select');
  Object.assign(s.style, {
    padding: '5px 8px', fontSize: '13px',
    background: '#1a1f2a', border: '1px solid #2a3140',
    color: '#d8dee9', borderRadius: '4px',
    fontFamily: 'inherit', cursor: 'pointer', flex: '1',
  } as Partial<CSSStyleDeclaration>);
  return s;
}

function makeLabel(text: string): HTMLDivElement {
  const d = document.createElement('div');
  d.textContent = text;
  d.style.minWidth = '160px';
  return d;
}

function makeValueLabel(text: string): HTMLDivElement {
  const d = document.createElement('div');
  d.textContent = text;
  d.style.minWidth = '44px';
  d.style.textAlign = 'right';
  d.style.fontVariantNumeric = 'tabular-nums';
  d.style.opacity = '0.85';
  return d;
}

function makeSlider(initial: number, onChange: (v: number) => void): HTMLInputElement {
  const s = document.createElement('input');
  s.type = 'range';
  s.min = '0';
  s.max = '100';
  s.value = String(Math.round(initial * 100));
  s.style.flex = '1';
  s.style.cursor = 'pointer';
  s.addEventListener('input', () => {
    onChange(Number(s.value) / 100);
  });
  return s;
}
